/**
 * Game Scene - FULLY RESPONSIVE with CENTERED CAMERA
 * 
 * Core gameplay with physics-based planet hopping.
 * Features:
 * - Custom orbital gravity physics
 * - Tap to launch mechanics
 * - Combo scoring system
 * - Object pooling for performance
 * - 3D-like planet visuals
 * - Particle effects for juice
 * - INFINITE planet generation with recycling
 * - RESPONSIVE: Works on any screen size
 * - IMPROVED CAMERA: Keeps ball centered on screen
 * - AUDIO: Heartbeat music and sound effects
 */

import Phaser from 'phaser';
import { 
  SCENES, 
  PHYSICS, 
  PLANET, 
  PLAYER, 
  SCORING, 
  DIFFICULTY,
  DEFAULT_SEED,
  UI
} from '../config/Constants';
import { PlatformManager } from '../sdk/PlatformManager';
import { SeededRandom } from '../utils/SeededRandom';
import { createPlanet3D, Planet3DSprites, animatePlanet3D, createPlayer3D } from '../systems/PlanetRenderer';
import { ParticleManager } from '../systems/ParticleManager';
import { AudioManager } from '../systems/AudioManager';

// Maximum time player can fly without landing before game over (lost in space)
const MAX_FLIGHT_TIME = 4000; // 4 seconds

enum PlayerState {
  ORBITING = 'orbiting',
  FLYING = 'flying',
  DEAD = 'dead'
}

interface PlanetData {
  sprites: Planet3DSprites;
  x: number;
  y: number;
  radius: number;
  color: number;
  active: boolean;
}

export class GameScene extends Phaser.Scene {
  private score = 0;
  private combo = 0;
  private isGameOver = false;
  private isPaused = false;
  
  private player!: Phaser.GameObjects.Container;
  private playerState: PlayerState = PlayerState.ORBITING;
  private playerVelocity = { x: 0, y: 0 };
  private orbitAngle = 0;
  private currentPlanet: PlanetData | null = null;
  private lastPlanet: PlanetData | null = null;
  private launchTime = 0;
  private resumeTime = 0; // Tracks when game was resumed to prevent accidental launch
  private lastTapTime = 0; // Tracks last tap for double-tap prevention
  
  private planets: PlanetData[] = [];
  private planetPool: PlanetData[] = [];
  
  private rng!: SeededRandom;
  private particleManager!: ParticleManager;
  private audioManager!: AudioManager;
  
  private scoreText!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private pauseButton!: Phaser.GameObjects.Container;
  private pauseOverlay!: Phaser.GameObjects.Container;
  
  private gameWidth = 720;
  private gameHeight = 1280;
  
  constructor() {
    super({ key: SCENES.GAME });
  }
  
  private get width(): number {
    return this.scale.width || this.gameWidth;
  }
  
  private get height(): number {
    return this.scale.height || this.gameHeight;
  }
  
  init(): void {
    this.score = 0;
    this.combo = 0;
    this.isGameOver = false;
    this.isPaused = false;
    this.playerState = PlayerState.ORBITING;
    this.playerVelocity = { x: 0, y: 0 };
    this.orbitAngle = 0;
    this.currentPlanet = null;
    this.lastPlanet = null;
    this.launchTime = 0;
    this.planets = [];
    this.planetPool = [];
    
    this.gameWidth = this.scale.width;
    this.gameHeight = this.scale.height;
    
    this.rng = new SeededRandom(DEFAULT_SEED);
    console.log('[GameScene] Initialized', { width: this.width, height: this.height });
  }
  
  create(): void {
    console.log('[GameScene] Creating game with audio...');
    
    PlatformManager.getInstance().gameplayStart();
    
    // Initialize audio and particle systems
    this.audioManager = AudioManager.getInstance(this);
    this.particleManager = new ParticleManager(this);
    this.scale.on('resize', this.handleResize, this);
    
    this.createBackground();
    this.createPlanetPool();
    this.createInitialPlanets();
    this.createPlayer();
    this.createUI();
    this.setupInput();
    
    this.cameras.main.fadeIn(300);
    
    // Start heartbeat background music
    this.audioManager.startMusic();
    
    console.log('[GameScene] Ready with heartbeat music!');
  }
  
  private createBackground(): void {
    this.add.rectangle(this.width / 2, this.height / 2, this.width * 2, this.height * 3, 0x050510)
      .setScrollFactor(0);
    
    const nebula = this.add.graphics();
    nebula.setScrollFactor(0.1);
    nebula.fillStyle(0x1a0a2e, 0.3);
    nebula.fillCircle(this.width * 0.3, this.height * 0.3, 400);
    nebula.fillStyle(0x0a1a2e, 0.3);
    nebula.fillCircle(this.width * 0.7, this.height * 0.7, 350);
    
    this.createStarLayer(100, 0.1, 0.3, 1);
    this.createStarLayer(80, 0.2, 0.5, 1.5);
    this.createStarLayer(50, 0.3, 0.7, 2);
  }
  
  private createStarLayer(count: number, scrollFactor: number, alphaBase: number, sizeBase: number): void {
    const stars = this.add.graphics();
    stars.setScrollFactor(scrollFactor);
    
    for (let i = 0; i < count; i++) {
      const alpha = alphaBase + this.rng.next() * 0.3;
      const size = sizeBase * (0.5 + this.rng.next() * 0.5);
      
      stars.fillStyle(0xffffff, alpha);
      stars.fillCircle(
        this.rng.next() * this.width * 1.5,
        this.rng.next() * this.height * 2 - this.height / 2,
        size
      );
    }
  }
  
  private setupInput(): void {
    this.input.on('pointerdown', () => {
      if (this.isGameOver || this.isPaused) return;
      if (this.playerState === PlayerState.ORBITING) {
        this.launch();
      }
    });
    
    this.input.keyboard?.on('keydown-SPACE', () => {
      if (this.isGameOver || this.isPaused) return;
      if (this.playerState === PlayerState.ORBITING) {
        this.launch();
      }
    });
  }
  
  private handleResize(gameSize: Phaser.Structs.Size): void {
    this.gameWidth = gameSize.width;
    this.gameHeight = gameSize.height;
    
    if (this.scoreText) {
      this.scoreText.setPosition(this.width / 2, 80);
    }
    if (this.comboText) {
      this.comboText.setPosition(this.width / 2, 140);
    }
  }
  
  private createPlanetPool(): void {
    for (let i = 0; i < PLANET.POOL_SIZE; i++) {
      const color = PLANET.COLORS[Math.floor(this.rng.next() * PLANET.COLORS.length)];
      
      const sprites = createPlanet3D(this, {
        x: 0,
        y: 0,
        radius: 50,
        color: color,
        hasRing: this.rng.next() > 0.7,
        hasAtmosphere: this.rng.next() > 0.5
      });
      
      sprites.container.setVisible(false);
      
      this.planetPool.push({
        sprites,
        x: 0,
        y: 0,
        radius: 50,
        color: color,
        active: false
      });
    }
  }
  
  private getPlanetFromPool(): PlanetData | null {
    for (const planet of this.planetPool) {
      if (!planet.active) {
        planet.active = true;
        planet.sprites.container.setVisible(true);
        return planet;
      }
    }
    console.warn('[GameScene] No planets available in pool!');
    return null;
  }
  
  private createInitialPlanets(): void {
    const first = this.createPlanet(this.width / 2, this.height / 2 + 200, 60);
    if (first) {
      this.currentPlanet = first;
      animatePlanet3D(this, first.sprites);
    }
    
    for (let i = 0; i < 5; i++) {
      this.generateNextPlanet();
    }
  }
  
  private createPlanet(x: number, y: number, radius: number): PlanetData | null {
    const planet = this.getPlanetFromPool();
    if (!planet) return null;
    
    const color = PLANET.COLORS[Math.floor(this.rng.next() * PLANET.COLORS.length)];
    
    planet.x = x;
    planet.y = y;
    planet.radius = radius;
    planet.color = color;
    
    planet.sprites.container.setPosition(x, y);
    
    planet.sprites.glow.setRadius(radius + 20);
    planet.sprites.body.setRadius(radius);
    planet.sprites.shadow.setRadius(radius * 0.95);
    planet.sprites.highlight.setRadius(radius * 0.6);
    
    planet.sprites.glow.setFillStyle(color, 0.15);
    planet.sprites.body.setFillStyle(this.darkenColor(color, 0.6));
    planet.sprites.highlight.setFillStyle(color, 0.5);
    
    animatePlanet3D(this, planet.sprites);
    
    this.planets.push(planet);
    return planet;
  }
  
  private darkenColor(hex: number, factor: number): number {
    const r = Math.floor(((hex >> 16) & 0xff) * factor);
    const g = Math.floor(((hex >> 8) & 0xff) * factor);
    const b = Math.floor((hex & 0xff) * factor);
    return (r << 16) | (g << 8) | b;
  }
  
  private generateNextPlanet(): void {
    const difficultyProgress = Math.min(this.score / DIFFICULTY.MAX_DIFFICULTY_SCORE, 1);
    
    // Check if mobile (narrow screen)
    const isMobile = this.width < 600;
    
    // INCREASED base distance for more satisfying jumps
    const baseDistance = DIFFICULTY.INITIAL_DISTANCE + 
      (DIFFICULTY.MAX_DISTANCE - DIFFICULTY.INITIAL_DISTANCE) * Math.pow(difficultyProgress, 0.7);
    
    // Mobile gets HIGHER minimum distance (200+) for better dopamine hit
    const minDistance = isMobile ? 220 : 180;
    const maxDistance = isMobile ? this.width * 0.85 : baseDistance * 1.1;
    
    const distance = Phaser.Math.Clamp(
      baseDistance + (this.rng.next() - 0.3) * 60,
      minDistance,
      maxDistance
    );
    
    // More varied angles on mobile for interesting paths
    const angleVariance = isMobile ? 0.55 : (0.6 + this.rng.next() * 0.2);
    const angle = -Math.PI / 2 + (this.rng.next() - 0.5) * Math.PI * angleVariance;
    
    const lastPlanet = this.planets[this.planets.length - 1];
    
    // VARIED planet sizes for visual interest
    // Random size category: small (30%), medium (40%), large (30%)
    const sizeRoll = this.rng.next();
    let radius: number;
    
    if (sizeRoll < 0.3) {
      // Small planet (40-55px)
      radius = 40 + this.rng.next() * 15;
    } else if (sizeRoll < 0.7) {
      // Medium planet (55-75px)
      radius = 55 + this.rng.next() * 20;
    } else {
      // Large planet (75-100px) - gets smaller with difficulty
      const maxR = PLANET.MAX_RADIUS - (difficultyProgress * 15);
      radius = 75 + this.rng.next() * (maxR - 75);
    }
    
    // Calculate position with margin
    const margin = radius + 60;
    let x = Phaser.Math.Clamp(
      lastPlanet.x + Math.cos(angle) * distance,
      margin,
      this.width - margin
    );
    
    // Ensure minimum vertical gap for satisfying arc
    const minVerticalGap = isMobile ? 150 + radius : 120 + radius;
    let y = lastPlanet.y - Math.max(Math.abs(Math.sin(angle) * distance), minVerticalGap) - 30;
    
    // Check for overlap with last planet - INCREASED gap (80px minimum)
    const dx = x - lastPlanet.x;
    const dy = y - lastPlanet.y;
    const actualDistance = Math.sqrt(dx * dx + dy * dy);
    const minSafeDistance = lastPlanet.radius + radius + 80; // 80px gap for visibility
    
    if (actualDistance < minSafeDistance) {
      // Push planet further away
      const scale = minSafeDistance / actualDistance;
      x = lastPlanet.x + dx * scale;
      y = lastPlanet.y + dy * scale;
      
      // Re-clamp X
      x = Phaser.Math.Clamp(x, margin, this.width - margin);
    }
    
    this.createPlanet(x, y, radius);
  }
  
  private createPlayer(): void {
    if (!this.currentPlanet) return;
    
    const startX = this.currentPlanet.x;
    const startY = this.currentPlanet.y - this.currentPlanet.radius - PLAYER.RADIUS;
    
    this.player = createPlayer3D(this, startX, startY, PLAYER.RADIUS, PLAYER.COLOR);
    this.orbitAngle = -Math.PI / 2;
  }
  
  private createUI(): void {
    // Device pixel ratio for crisp text
    const textRes = Math.min(window.devicePixelRatio || 1, 2);
    
    // Score shadow
    this.add.text(this.width / 2 + 3, 83, '0', {
      fontSize: '64px',
      fontFamily: UI.FONT_FAMILY,
      color: '#000000',
      fontStyle: 'bold',
      resolution: textRes
    }).setOrigin(0.5).setScrollFactor(0).setDepth(99).setAlpha(0.3);
    
    // Score text
    this.scoreText = this.add.text(this.width / 2, 80, '0', {
      fontSize: '64px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffffff',
      fontStyle: 'bold',
      resolution: textRes
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100);
    
    // Combo text
    this.comboText = this.add.text(this.width / 2, 140, '', {
      fontSize: '28px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffcc00',
      fontStyle: 'bold',
      resolution: textRes
    }).setOrigin(0.5).setScrollFactor(0).setDepth(100);
    
    // Pause button (top-right corner)
    this.createPauseButton();
    
    // Pause overlay (hidden initially)
    this.createPauseOverlay();
  }
  
  private createPauseButton(): void {
    this.pauseButton = this.add.container(this.width - 50, 50);
    this.pauseButton.setScrollFactor(0).setDepth(150);
    
    // Button background
    const bg = this.add.circle(0, 0, 25, 0x000000, 0.5);
    
    // Pause icon (two bars)
    const icon = this.add.text(0, 0, '⏸', {
      fontSize: '28px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    this.pauseButton.add([bg, icon]);
    this.pauseButton.setSize(50, 50);
    this.pauseButton.setInteractive({ useHandCursor: true });
    
    this.pauseButton.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      this.togglePause();
    });
    
    // Hover effect
    this.pauseButton.on('pointerover', () => {
      bg.setFillStyle(0x333333, 0.7);
    });
    this.pauseButton.on('pointerout', () => {
      bg.setFillStyle(0x000000, 0.5);
    });
  }
  
  private createPauseOverlay(): void {
    // Create pause overlay as a group of elements (not container) for proper input handling
    const centerX = this.width / 2;
    const centerY = this.height / 2;
    
    // Dark overlay background
    const overlayBg = this.add.rectangle(centerX, centerY, this.width * 2, this.height * 2, 0x000000, 0.7);
    overlayBg.setScrollFactor(0).setDepth(200);
    
    // Device pixel ratio for crisp text
    const textRes = Math.min(window.devicePixelRatio || 1, 2);
    
    // Paused text
    const pausedText = this.add.text(centerX, centerY - 80, 'PAUSED', {
      fontSize: '48px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffffff',
      fontStyle: 'bold',
      resolution: textRes
    }).setOrigin(0.5).setScrollFactor(0).setDepth(201);
    
    // Resume button background
    const resumeBg = this.add.rectangle(centerX, centerY + 20, 200, 60, 0x00cc66, 1);
    resumeBg.setScrollFactor(0).setDepth(202);
    resumeBg.setInteractive({ useHandCursor: true });
    
    resumeBg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      console.log('[GameScene] Resume clicked!')
      this.togglePause();
    });
    resumeBg.on('pointerover', () => resumeBg.setFillStyle(0x00ff88));
    resumeBg.on('pointerout', () => resumeBg.setFillStyle(0x00cc66));
    
    // Resume text
    const resumeText = this.add.text(centerX, centerY + 20, '▶ RESUME', {
      fontSize: '28px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffffff',
      fontStyle: 'bold',
      resolution: textRes
    }).setOrigin(0.5).setScrollFactor(0).setDepth(203);
    
    // Hint text
    const hintText = this.add.text(centerX, centerY + 100, 'Press ESC or P to resume', {
      fontSize: '18px',
      fontFamily: UI.FONT_FAMILY,
      color: '#aaaaaa',
      resolution: textRes
    }).setOrigin(0.5).setScrollFactor(0).setDepth(201);
    
    // Store all elements in a container for easy show/hide
    this.pauseOverlay = this.add.container(0, 0, [overlayBg, pausedText, resumeBg, resumeText, hintText]);
    this.pauseOverlay.setDepth(200).setVisible(false);
    
    // Keyboard shortcuts for pause
    this.input.keyboard?.on('keydown-ESC', () => this.togglePause());
    this.input.keyboard?.on('keydown-P', () => this.togglePause());
  }
  
  private togglePause(): void {
    if (this.isGameOver) return;
    
    this.isPaused = !this.isPaused;
    this.pauseOverlay.setVisible(this.isPaused);
    
    // Update pause button icon
    const icon = this.pauseButton.list[1] as Phaser.GameObjects.Text;
    icon.setText(this.isPaused ? '▶' : '⏸');
    
    if (this.isPaused) {
      this.audioManager.stopMusic();
    } else {
      this.audioManager.startMusic();
      this.resumeTime = this.time.now; // Track resume time to prevent accidental launch
    }
    
    this.audioManager.playSFX('ui');
  }
  
  private launch(): void {
    if (!this.currentPlanet) return;
    
    // Double-tap prevention
    if (this.time.now - this.lastTapTime < PHYSICS.TAP_COOLDOWN) return;
    this.lastTapTime = this.time.now;
    
    // Prevent accidental launch right after resuming (200ms cooldown)
    if (this.time.now - this.resumeTime < 200) return;
    
    this.lastPlanet = this.currentPlanet;
    this.launchTime = this.time.now;
    
    let launchAngle = this.orbitAngle + Math.PI / 2;
    
    const launchDirY = Math.sin(launchAngle);
    if (launchDirY > 0.2) {
      launchAngle = this.orbitAngle - Math.PI / 2;
    }
    
    const speed = PHYSICS.LAUNCH_POWER;
    
    this.playerVelocity.x = Math.cos(launchAngle) * speed;
    this.playerVelocity.y = Math.sin(launchAngle) * speed;
    
    this.playerState = PlayerState.FLYING;
    this.currentPlanet = null;
    
    this.particleManager.createLaunchBurst({
      x: this.player.x,
      y: this.player.y,
      color: PLAYER.COLOR,
      count: 15
    });
    this.particleManager.createScreenShake(3, 50);
    
    // Play launch sound
    this.audioManager.playSFX('launch');
    
    console.log('[GameScene] Launched!');
  }
  
  update(_time: number, delta: number): void {
    if (this.isGameOver || this.isPaused) return;
    
    // Clamp delta time to prevent physics explosion on lag spikes
    const dt = Math.min(delta / 1000, PHYSICS.MAX_DELTA);
    
    if (this.playerState === PlayerState.ORBITING) {
      this.updateOrbiting(dt);
    } else if (this.playerState === PlayerState.FLYING) {
      this.updateFlying(dt);
      // Pass velocity to trail for color gradient (cyan = slow, orange = fast)
      const velocity = Math.sqrt(this.playerVelocity.x ** 2 + this.playerVelocity.y ** 2);
      this.particleManager.updateTrail(this.player.x, this.player.y, PLAYER.COLOR, true, velocity);
      
      // Check for lost in space - too long flying without landing
      const timeSinceLaunch = this.time.now - this.launchTime;
      if (timeSinceLaunch > MAX_FLIGHT_TIME) {
        console.log('[GameScene] Lost in space!');
        this.lostInSpace();
        return;
      }
    }
    
    this.updateCamera(dt);
    this.checkBounds();
    this.generatePlanetsAhead();
    this.recyclePlanets();
  }
  
  private updateOrbiting(dt: number): void {
    if (!this.currentPlanet) return;
    
    this.orbitAngle += PHYSICS.ORBIT_SPEED * dt;
    
    const orbitRadius = this.currentPlanet.radius + PLAYER.RADIUS + 5;
    this.player.x = this.currentPlanet.x + Math.cos(this.orbitAngle) * orbitRadius;
    this.player.y = this.currentPlanet.y + Math.sin(this.orbitAngle) * orbitRadius;
  }
  
  private updateFlying(dt: number): void {
    const timeSinceLaunch = this.time.now - this.launchTime;
    const isImmune = timeSinceLaunch < PHYSICS.LAUNCH_IMMUNITY_TIME;
    
    for (const planet of this.planets) {
      if (!planet.active) continue;
      
      if (isImmune && planet === this.lastPlanet) {
        continue;
      }
      
      const dx = planet.x - this.player.x;
      const dy = planet.y - this.player.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance < planet.radius + PLAYER.RADIUS + PHYSICS.STICK_THRESHOLD) {
        if (isImmune && planet === this.lastPlanet) {
          continue;
        }
        this.landOnPlanet(planet);
        return;
      }
      
      const gravityFieldRadius = planet.radius * PLANET.GRAVITY_FIELD_MULTIPLIER;
      if (distance < gravityFieldRadius && distance > planet.radius) {
        const strength = PHYSICS.GRAVITY_STRENGTH * (planet.radius / 50) / 
          Math.max(distance * distance / 10000, 1);
        
        this.playerVelocity.x += (dx / distance) * strength * dt;
        this.playerVelocity.y += (dy / distance) * strength * dt;
      }
    }
    
    this.playerVelocity.x *= PHYSICS.SPACE_DRAG;
    this.playerVelocity.y *= PHYSICS.SPACE_DRAG;
    
    const speed = Math.sqrt(this.playerVelocity.x ** 2 + this.playerVelocity.y ** 2);
    if (speed > PHYSICS.MAX_VELOCITY) {
      const scale = PHYSICS.MAX_VELOCITY / speed;
      this.playerVelocity.x *= scale;
      this.playerVelocity.y *= scale;
    }
    
    this.player.x += this.playerVelocity.x * dt;
    this.player.y += this.playerVelocity.y * dt;
  }
  
  private landOnPlanet(planet: PlanetData): void {
    const planetIndex = this.planets.indexOf(planet);
    const lastIndex = this.lastPlanet ? this.planets.indexOf(this.lastPlanet) : -1;
    
    this.particleManager.createLandingImpact({
      x: this.player.x,
      y: this.player.y,
      color: planet.color
    });
    this.particleManager.createScreenShake(2, 30);
    
    // Play landing sound
    this.audioManager.playSFX('land');
    
    // Haptic feedback for mobile (subtle vibration)
    if (navigator.vibrate) {
      navigator.vibrate(15);
    }
    
    if (planetIndex > lastIndex) {
      // Check for PERFECT landing (fast landing within time window)
      const landingTime = this.time.now - this.launchTime;
      const isPerfect = landingTime < SCORING.PERFECT_WINDOW;
      
      let points = SCORING.BASE_POINTS + this.combo * SCORING.COMBO_BONUS;
      
      // Perfect landing bonus
      if (isPerfect) {
        points += SCORING.PERFECT_BONUS;
        this.showPerfectLanding();
      }
      
      this.score += points;
      this.combo = Math.min(this.combo + 1, SCORING.MAX_COMBO);
      this.updateScoreDisplay();
      
      this.particleManager.createScorePopup(
        this.player.x,
        this.player.y - 40,
        points,
        this.combo
      );
      
      if (this.score > (this.registry.get('highScore') || 0)) {
        this.registry.set('highScore', this.score);
        localStorage.setItem('sticky_planet_high_score', String(this.score));
      }
      
      // Play combo sound for streaks
      if (this.combo > 1) {
        this.audioManager.playSFX('combo');
      }
      
      // Screen flash on high combos for extra juice
      if (this.combo >= 3) {
        this.cameras.main.flash(100, 255, 255, 255, false, (_cam: Phaser.Cameras.Scene2D.Camera, progress: number) => {
          if (progress >= 1) return;
        });
        // Stronger haptic for combo
        if (navigator.vibrate) {
          navigator.vibrate([10, 30, 20]);
        }
      }
      
      // Camera zoom on high combos (5+) for extra impact
      if (this.combo >= 5) {
        this.tweens.add({
          targets: this.cameras.main,
          zoom: 1.05,
          duration: 150,
          yoyo: true,
          ease: 'Quad.easeOut'
        });
      }
      
      console.log('[GameScene] Landed on NEW planet!', { score: this.score, combo: this.combo, perfect: isPerfect });
    } else {
      this.combo = 0;
    }
    
    this.currentPlanet = planet;
    this.playerState = PlayerState.ORBITING;
    
    const dx = this.player.x - planet.x;
    const dy = this.player.y - planet.y;
    this.orbitAngle = Math.atan2(dy, dx);
    
    this.playerVelocity = { x: 0, y: 0 };
  }
  
  /**
   * Lost in space - plays falling sound then ends game quickly
   */
  private lostInSpace(): void {
    if (this.isGameOver) return;
    
    // Play falling sound
    this.audioManager.playSFX('falling');
    
    // Quick transition to game over (300ms)
    this.time.delayedCall(300, () => {
      this.gameOver();
    });
  }
  
  private updateCamera(dt: number): void {
    // Keep player centered vertically
    const targetY = this.player.y - this.height * 0.5;
    
    const lerpSpeed = 5 * dt;
    this.cameras.main.scrollY = Phaser.Math.Linear(
      this.cameras.main.scrollY,
      targetY,
      lerpSpeed
    );
    
    // ALWAYS track player horizontally (essential for mobile)
    const targetX = this.player.x - this.width * 0.5;
    this.cameras.main.scrollX = Phaser.Math.Linear(
      this.cameras.main.scrollX,
      targetX,
      lerpSpeed
    );
  }
  
  private updateScoreDisplay(): void {
    this.scoreText.setText(String(this.score));
    
    if (this.combo > 1) {
      this.comboText.setText(`${this.combo}x Combo!`);
      
      this.tweens.add({
        targets: this.comboText,
        scaleX: { from: 1.3, to: 1 },
        scaleY: { from: 1.3, to: 1 },
        duration: 200,
        ease: 'Back.out'
      });
    }
    
    this.tweens.add({
      targets: this.scoreText,
      scaleX: { from: 1.2, to: 1 },
      scaleY: { from: 1.2, to: 1 },
      duration: 150,
      ease: 'Back.out'
    });
  }
  
  private showPerfectLanding(): void {
    // Create PERFECT! popup
    const textRes = Math.min(window.devicePixelRatio || 1, 2);
    
    const perfect = this.add.text(this.player.x, this.player.y - 80, '⭐ PERFECT! ⭐', {
      fontSize: '32px',
      fontFamily: 'Segoe UI, system-ui, sans-serif',
      color: '#ffcc00',
      fontStyle: 'bold',
      stroke: '#ff6600',
      strokeThickness: 3,
      resolution: textRes
    }).setOrigin(0.5).setDepth(200);
    
    // Animate popup
    this.tweens.add({
      targets: perfect,
      y: perfect.y - 50,
      scaleX: { from: 0.5, to: 1.2 },
      scaleY: { from: 0.5, to: 1.2 },
      alpha: { from: 1, to: 0 },
      duration: 1000,
      ease: 'Cubic.out',
      onComplete: () => perfect.destroy()
    });
    
    // Extra haptic for perfect
    if (navigator.vibrate) {
      navigator.vibrate([20, 50, 30]);
    }
    
    // Golden flash
    this.cameras.main.flash(150, 255, 204, 0);
  }
  
  private checkBounds(): void {
    const cameraLeft = this.cameras.main.scrollX;
    const cameraRight = this.cameras.main.scrollX + this.width;
    
    if (this.player.x < cameraLeft - 150 || this.player.x > cameraRight + 150) {
      this.lostInSpace();
    }
    
    if (this.player.y > this.cameras.main.scrollY + this.height + 200) {
      this.lostInSpace();
    }
  }
  
  private generatePlanetsAhead(): void {
    const lastPlanet = this.planets[this.planets.length - 1];
    if (lastPlanet && lastPlanet.y > this.cameras.main.scrollY - 500) {
      this.generateNextPlanet();
    }
  }
  
  private recyclePlanets(): void {
    const recycleThreshold = this.cameras.main.scrollY + this.height + 500;
    
    for (let i = this.planets.length - 1; i >= 0; i--) {
      const planet = this.planets[i];
      
      if (planet === this.currentPlanet || planet === this.lastPlanet) {
        continue;
      }
      
      if (planet.y > recycleThreshold) {
        planet.sprites.container.setVisible(false);
        planet.active = false;
        
        this.planets.splice(i, 1);
      }
    }
  }
  
  private gameOver(): void {
    if (this.isGameOver) return;
    this.isGameOver = true;
    
    PlatformManager.getInstance().gameplayStop();
    
    // Play death explosion sound and stop music
    this.audioManager.playSFX('death');
    this.audioManager.stopMusic();
    
    this.particleManager.createGameOverEffect(this.player.x, this.player.y);
    
    this.registry.set('lastScore', this.score);
    
    console.log('[GameScene] Game Over! Score:', this.score);
    
    this.time.delayedCall(300, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start(SCENES.GAME_OVER, { score: this.score });
      });
    });
  }
  
  shutdown(): void {
    this.scale.off('resize', this.handleResize, this);
    if (this.particleManager) {
      this.particleManager.destroy();
    }
  }
}

export default GameScene;
